import React, { useRef, useState } from 'react'
import { useMutation, useApolloClient } from '@apollo/client/react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { ButtonColumn, Text } from '../../components/layout'
import { SelectField } from '../../components/layout/form/style/ReactSelect'
import { useSettings } from '../Parameters'
import { CHANGE_COMPANY, ChangeCompanyResponse } from './mutation'
import { companySwitcherMessages } from './messages'
import { storeLogin, getStoredCompanyId } from '../../graphql-client/storedState'
import { notifyUserDataChanged } from '../../http-client'
import { setSuccessNotification, setErrorNotification } from '../../components/notification'
import { setErrorNotifications } from '../../components/responses/setErrorNotifications'
import { formatMessageSafe } from '../../util/translateInputOptions'
import { CompanySwitchingModal } from './CompanySwitchingModal'

const SwitcherRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.25rem;
  }
`

const SelectContainer = styled.div`
  min-width: 220px;
`

interface CompanyOption {
  value: number
  label: string
}

const MODAL_MIN_DURATION_MS = 2000

export const CompanySwitcher: React.FunctionComponent = () => {
  const intl = useIntl()
  const formatMessage = formatMessageSafe(intl)
  const settings = useSettings()
  const client = useApolloClient()
  const [pendingCompanyId, setPendingCompanyId] = useState<number | null>(null)
  const [showModal, setShowModal] = useState(false)
  const switchStartedAt = useRef(0)

  const finishSwitching = (callback: () => void) => {
    const elapsed = Date.now() - switchStartedAt.current
    const remaining = Math.max(MODAL_MIN_DURATION_MS - elapsed, 0)
    setTimeout(() => {
      callback()
      setShowModal(false)
    }, remaining)
  }

  const [changeCompany] = useMutation<ChangeCompanyResponse>(CHANGE_COMPANY, {
    onCompleted(response) {
      const { changeCompany } = response
      finishSwitching(() => {
        if (changeCompany.__typename === 'ChangeCompanySuccess') {
          const { accessToken, refreshToken, locationToken } = changeCompany
          storeLogin({ accessToken, refreshToken, locationToken })
          notifyUserDataChanged(accessToken)

          client.refetchQueries({ include: 'active' })

          setSuccessNotification(
            formatMessage(companySwitcherMessages.success_title),
            formatMessage(companySwitcherMessages.success_message)
          )
        } else {
          setErrorNotifications({ data: changeCompany })
        }
        setPendingCompanyId(null)
      })
    },
    onError(error) {
      console.error(error)
      finishSwitching(() => {
        setErrorNotification(
          formatMessage(companySwitcherMessages.error_title),
          formatMessage(companySwitcherMessages.error_message)
        )
        setPendingCompanyId(null)
      })
    },
  })

  const companies = settings?.companies ?? []

  if (companies.length <= 1) {
    return null
  }

  const options: CompanyOption[] = companies.map(company => ({ value: company.id, label: company.name }))
  const storedCompanyId = getStoredCompanyId()
  const currentCompanyId = pendingCompanyId ?? (storedCompanyId ? Number(storedCompanyId) : companies[0].id)
  const currentOption = options.find(option => option.value === currentCompanyId) ?? null
  const pendingOption = options.find(option => option.value === pendingCompanyId) ?? null

  return (
    <ButtonColumn>
      <SwitcherRow>
        <Text size="m">{formatMessage(companySwitcherMessages.label)}</Text>
        <SelectContainer>
          <SelectField
            classNamePrefix="Select"
            isDisabled={showModal}
            isSearchable={options.length > 5}
            isClearable={false}
            options={options}
            value={currentOption}
            onChange={(option: CompanyOption | null) => {
              if (!option) return
              setPendingCompanyId(option.value)
              switchStartedAt.current = Date.now()
              setShowModal(true)
              changeCompany({ variables: { companyId: option.value } })
            }}
          />
        </SelectContainer>
      </SwitcherRow>
      {showModal && <CompanySwitchingModal companyName={pendingOption?.label ?? ''} />}
    </ButtonColumn>
  )
}
